import ROUTES from '../../../_constants/routes'
import { getAllowedDob } from '../../../_helpers'
import {
  todayIsoDate,
  BUSINESS_ENTITY_OPTIONS,
  FINANCIAL_RADIO_OPTIONS,
  FURTHER_DETAILS_FIELD,
  INDUSTRY_OPTIONS,
} from '../../form.constants'

const republic_and_fastar_config = {
  crumbs: [
    {
      label: 'Your Invoices',
      link: ROUTES.upload_invoices,
    },
    {
      label: 'Business Details',
      link: ROUTES.confirm_form,
    },
    {
      label: 'Submitted',
      link: ROUTES.submitted,
    },
  ],
  sections: [
    {
      title: 'Contact Details',
      type: 'contact_details',
      tooltip: 'Please tell us about your business.',
      fields: [
        {
          type: 'text',
          name: 'business_name',
          table: 'business',
          placeholder: 'Legal Business Name',
          label: 'Legal Business Name',
          required: true,
        },
        {
          type: 'select',
          name: 'business_entity',
          table: 'business',
          placeholder: 'Type of Entity',
          label: 'Type of Entity',
          options: BUSINESS_ENTITY_OPTIONS,
          required: true,
        },
        {
          type: 'region',
          name: 'inc_state',
          table: 'business',
          label: 'State of Incorporation',
        },
        [
          {
            type: 'text',
            name: 'address_street',
            table: 'business',
            placeholder: 'Street',
            label: 'Business Street Address',
            required: true,
          },
          {
            type: 'text',
            name: 'address_unit',
            table: 'business',
            placeholder: 'Unit',
            required: false,
          },
          {
            type: 'text',
            name: 'address_city',
            table: 'business',
            placeholder: 'City',
            label: 'City',
            required: true,
          },
          {
            type: 'region',
            name: 'address_state',
            table: 'business',
            placeholder: 'State',
            label: 'State',
            required: true,
          },
          {
            type: 'text',
            name: 'address_zip_code',
            table: 'business',
            placeholder: 'Zip Code',
            label: 'Zip Code',
            required: true,
          },
        ],
        {
          type: 'tel',
          name: 'business_phone_number',
          table: 'business',
          lenderRequired: true,
          placeholder: 'Company Phone Number',
          label: 'Company Phone Number',
          required: true,
        },
        {
          type: 'text',
          name: 'business_website',
          table: 'business',
          placeholder: 'Website',
          label: 'Website',
          required: false,
        },
      ],
    },
    {
      title: 'Business Officer(s)',
      type: 'business_officers',
      label: 'Business Officer/Owner',
      custom_observation:
        '* It is required that every person in the business with ownership equal or higher than 20% is listed as a Business Officer',
      fields: [
        {
          type: 'text',
          name: 'first_name',
          table: 'business_officers',
          label: 'First Name',
          placeholder: 'First Name',
          required: true,
        },
        {
          type: 'text',
          name: 'last_name',
          table: 'business_officers',
          label: 'Last Name',
          placeholder: 'Last Name',
          required: true,
        },
        {
          type: 'text',
          name: 'title',
          table: 'business_officers',
          label: 'Title',
          placeholder: 'Title',
          required: true,
        },
        {
          type: 'percentage',
          name: 'pct_ownership',
          table: 'business_officers',
          label: 'Percent Ownership',
          placeholder: 'Percent Ownership',
          required: true,
        },
        {
          type: 'tel',
          name: 'phone_number',
          table: 'business_officers',
          label: 'Home Telephone #',
          placeholder: 'Home Telephone #',
          required: true,
        },
        {
          type: 'ssn',
          name: 'ssn_last4',
          table: 'business_officers',
          label: 'SSN',
          placeholder: 'SSN',
          required: true,
        },
        {
          type: 'email',
          name: 'email_address',
          table: 'business_officers',
          label: 'Email',
          placeholder: 'Email',
          required: true,
        },
        {
          type: 'date',
          name: 'dob',
          max: getAllowedDob(18),
          table: 'business_officers',
          label: 'Date of Birth (MM-DD-YYYY)',
          placeholder: 'Date of Birth (MM-DD-YYYY)',
          required: true,
        },
        {
          type: 'text',
          name: 'address',
          table: 'business_officers',
          label: 'Address',
          placeholder: 'Address',
          required: true,
        },
        {
          type: 'upload_document',
          name: 'drivers_license_upload',
          table: 'deal',
          label: `Copy of Driver's License`,
          required: false,
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
      ],
    },
    {
      title: 'Business Details',
      tooltip: 'Please give us a sense of your size and business operations.',
      fields: [
        {
          type: 'business_ein',
          name: 'business_ein_last4',
          table: 'business',
          label: 'Federal Tax ID (EIN)',
          tooltip:
            'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
        },
        {
          type: 'date',
          name: 'date_business_started',
          table: 'business',
          label: 'Date Business Incorporated',
          max: todayIsoDate,
        },
        {
          type: 'select',
          name: 'business_industry',
          table: 'business',
          label: 'Type of Business',
          options: INDUSTRY_OPTIONS,
          required: true,
        },
        {
          type: 'currency',
          name: 'prev_yr_rev',
          table: 'business',
          label: 'Estimated Annual Revenue (Previous 12 Months)',
          placeholder: 'Previous 12 Months',
          required: true,
        },
        {
          type: 'currency',
          name: 'next_12m_rev',
          table: 'business',
          label: 'Estimated Annual Revenue (Next 12 Months)',
          placeholder: 'Next 12 Months',
          required: true,
        },
        {
          type: 'number',
          name: 'total_num_customers',
          table: 'business',
          label: 'Total Number of Customers',
          required: true,
        },
        {
          type: 'currency',
          name: 'avg_invoice_value',
          label: 'Average Invoice Amount',
          table: 'business',
          required: true,
        },
      ],
    },
    {
      title: 'Largest Customers',
      section_title: 'Customers for Funding',
      type: 'largest_customers',
      maxLength: 5,
      addButtonInitialLabel: 'Add Largest 5 Customers +',
      addButtonLabel: 'Add Next Customer +',
      label: 'Customer',
      required: true,
      fields: [
        {
          type: 'text',
          name: 'debtor_name',
          label: 'Customer Name',
          table: 'top_business_debtors',
          required: true,
        },
        {
          type: 'text',
          name: 'debtor_address_street',
          label: 'Customer Address',
          table: 'top_business_debtors',
          required: true,
        },
      ],
    },
    {
      title: 'Financial Details',
      tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
      fields: [
        {
          type: 'currency',
          name: 'financing_required_amount',
          table: 'business',
          label: 'How much would you like to borrow?',
          required: true,
        },
        {
          type: 'date',
          name: 'financing_required_date',
          table: 'business',
          label: 'When do you need it by?',
          required: true,
        },
        {
          type: 'radio',
          name: 'currently_being_financed',
          table: 'business',
          required: true,
          label: 'Does the business currently have financing?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'currently_being_financed_explain',
            table: 'business',
            label: 'Please Explain',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_outstanding_commercial_loans',
          table: 'business',
          required: true,
          label: 'Does the business currently have loans outstanding?',
          options: FINANCIAL_RADIO_OPTIONS,
          tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'outstanding_commercial_loans_explain',
            table: 'business',
            label: 'Further details:',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_delinquent_taxes',
          table: 'business',
          label: 'Are there any delinquent taxes (Federal, State, or Payroll)?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'delinquent_taxes_explain',
            table: 'business',
            label: 'Further details:',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_officer_felonies',
          label: 'Have any of the company Owners/Officers been convicted of a felony?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'has_officer_felonies_explain',
            table: 'business',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_officer_bankruptcies',
          label: 'Has the company or its principals ever filed bankruptcy?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: { ...FURTHER_DETAILS_FIELD, name: 'has_officer_bankruptcies_explain' },
        },
        {
          type: 'radio',
          name: 'has_different_company_name',
          table: 'business',
          label: 'Has the company ever operated under a different name?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'has_different_company_name_explain',
            table: 'additional_business_data',
            label: 'Further details',
          },
        },
        {
          type: 'radio',
          name: 'has_associated_entities',
          table: 'business',
          label: 'Does the company have any associated entities?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            ...FURTHER_DETAILS_FIELD,
            name: 'associated_entities_explain',
          },
          tooltip: 'List any parent companies, subsidiaries and other affiliated entities.',
        },
      ],
    },
    {
      title: 'Document Details',
      type: 'upload_documents',
      tooltip:
        'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
      fields: [
        {
          type: 'upload_document',
          name: 'bank_statement',
          table: 'deal',
          label: 'Bank Statement from last 3 months',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
        {
          type: 'upload_document',
          name: 'aged_receivables',
          table: 'deal',
          label: 'A/R Aging',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
        {
          type: 'upload_document',
          name: 'sample_invoice_docs',
          table: 'deal',
          label: 'Sample Invoice Docs',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
          tooltip: 'Include invoices, purchase orders, bills of lading and contracts.',
        },
        {
          type: 'upload_document',
          name: 'articles_of_incorporation',
          table: 'deal',
          label: 'Articles of Incorporation',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
        {
          type: 'upload_document',
          name: 'corporate_bylaws',
          table: 'deal',
          label: 'Corporate Bylaws/Operating Agreement',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
        {
          type: 'upload_document',
          name: 'filing_941',
          table: 'deal',
          label: 'Recent 941 Filing',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
        {
          type: 'upload_document',
          name: 'proof_of_payment',
          table: 'deal',
          label: '941 Filing Proof of payment',
          options: [
            {
              label: 'Upload file',
              value: 'upload',
            },
            {
              label: 'I will do it later',
              value: 'later',
            },
          ],
        },
      ],
    },
  ],
  disclaimer_more: {
    btn_txt: 'Click to read Authorization of each Owner/Applicant',
    title: '',
    body: 'Each of the undersigned owner(s) submit this application for a financing facility on behalf of the applicant business. Each of the undersigned certify that there are no misrepresentations in this application or in any documents submitted in connection therewith, that all such information and documentation submitted is true, complete and accurate and does not omit any material information. The undersigned each agree that any funds made available pursuant to a financing facility will used only for the applicant’s working capital and not for any illegal purpose. By each undersigned’s signature below, Republic Business Credit, LLC (“RBC”) is hereby authorized to obtain (i) a consumer credit report through a credit agency chosen by RBC for purposes of RBC’s due diligence as part of the credit approval process of the financing facility for the applicant; and (ii) information from any third party and to make any other investigation of credit, either directly or indirectly through any agent of RBC on applicant and/or each of the undersigned. Each of the undersigned grants permission for the release and/or disclosure of financial information to a credit reporting agency or other third party as to RBC’s experience or transactions with applicant and/or the undersigned. Each of the undersigned understands that RBC will retain and rely on this application and any other credit or financial information RBC receives, even if a financing facility is not approved. These representations and authorizations and the documents submitted in connection with or related to this application and any potential facility may be relied on by RBC, any insurer of credit and any third party to whom RBC may sell / assign all or part of a financing facility. The undersigned each further authorize RBC to provide and/or disclosure to any such insurer or third party any information and documentation that such party may request with respect to the application or facility. If an adverse decision is made, in whole or in part, due to the information on a consumer report, a summary of the respective undersigned’s rights under the Fair Credit Reporting Act and the source of the information will be provided by RBC.',
  },
  agree_btn: false,
}
export { republic_and_fastar_config as default }
